import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, NEVER, Observable, throwError } from 'rxjs';
import { AuthService } from '@core/services/auth.service';
import { catchError, mergeMap, switchMap, retry, take } from 'rxjs/operators';
import { environment } from '@env/environment';
import { UserState } from '@shared/interfaces';
import { Router } from '@angular/router';
import { CartService } from '@core/services/cart.service';
import { isPlatformServer } from '@angular/common';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
	// Refresh Token Subject tracks the current token, or is null if no token is currently
	// available (e.g. refresh pending).

	private authUrl = environment.onDemand.baseUrl + environment.onDemand.apiAuth;
	private apiUrl =
		environment.onDemand.baseUrl + environment.onDemand.apiUser ||
		environment.onDemand.baseUrl + environment.onDemand.apiForgotPwd ||
		environment.onDemand.baseUrl + environment.onDemand.apiResetPwd;
	private createAccount = environment.onDemand.baseUrl + environment.onDemand.createAcccountApiUser
	private forgotPassUrl =
		environment.onDemand.baseUrl + environment.onDemand.apiForgotPwd;
	private resetPassUrl =
		environment.onDemand.baseUrl + environment.onDemand.apiResetPwd;

	private credentials: string;

	public reqWithHeaders;

	constructor(
		private authService: AuthService,
		private router: Router,
		private cartService: CartService,
		@Inject(PLATFORM_ID) private platformId: any
	) {}

	addAuthToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
		const contentType =
			req.detectContentTypeHeader() ===
			'application/x-www-form-urlencoded;charset=UTF-8'
				? 'application/x-www-form-urlencoded;charset=UTF-8'
				: 'application/json';

		return req.clone({
			setHeaders: {
				Authorization: `Bearer ${token}`,
				'Content-Type': contentType,
			//	'Content-Security-Policy': "script-src 'none'; object-src 'none'",
			},
		});
	}

	intercept(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		this.reqWithHeaders = request;

		const authRequest = request.url.includes(this.authUrl);
		const apiRequest = request.url.includes(this.apiUrl) || request.url.includes(this.createAccount);
		const loginRequest = request.url.includes(
			`grant_type=${environment.onDemand.grant_types.password}`
		);
		const forgotPassRequest = request.url.includes(this.forgotPassUrl);
		const resetPassRequest = request.url.includes(this.resetPassUrl);

		// Skip all of this if this is SSR...
		if (!!isPlatformServer(this.platformId)) {
			return next.handle(this.reqWithHeaders).pipe(
				catchError(() => EMPTY)
			);
		}

		this.credentials = btoa(
			`${environment.onDemand.clientId}:${environment.onDemand.secret}`
		);

		if (authRequest) {
			this.reqWithHeaders = request.clone({
				setHeaders: {
					Authorization: `Basic ${this.credentials}`,
					'Content-Type': 'application/x-www-form-urlencoded',
				},
			});
			return next.handle(this.reqWithHeaders);
		}


		if (apiRequest || forgotPassRequest || resetPassRequest) {
			return this.authService.getUserState$().pipe(
				take(1),
				mergeMap(({ anonymous, token_gen, access_token }: UserState) => {
					if (!anonymous && this.authService.timeToRefreshToken(token_gen)) {
						return this.cartService.logOutUser().pipe(
							switchMap(() => {
								this.router.navigate(['login']);
								return NEVER;
							})
						);
					}

					return next.handle(this.addAuthToken(request, access_token)).pipe(
						catchError((error: HttpErrorResponse) => {
							if (error.status === 401) {
								return this.authService.getAuthToken$().pipe(
									take(1),
									switchMap((newState: UserState) => {
										if (!newState?.access_token || newState?.error) {
											return throwError(() => error);
										}
										return next.handle(this.addAuthToken(request, newState?.access_token));
									})
								);
							}
							return throwError(() => error);
						})
					);
				})
			);
		}
		return next.handle(this.reqWithHeaders).pipe();
	}
}
